// components/admin/ImagePicker.tsx
/**
 * Image picker for selecting a building hero image
 * Shows available images from image library and allows upload
 */
import { useState, useRef, useEffect } from 'react';
import { Upload, Check, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { getImageLibrary, uploadImages, type ImageLibraryItem, type ImageCategory } from '../../lib/api';

interface ImagePickerProps {
  buildingId: string;
  selectedImageId: string | null;
  onSelect: (imageId: string | null, imageUrl: string | null) => void;
  category?: ImageCategory;
}

export function ImagePicker({
  buildingId,
  selectedImageId,
  onSelect,
  category = 'exterior',
}: ImagePickerProps) {
  const [images, setImages] = useState<ImageLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function fetchImages() {
      setLoading(true);
      const { data, error } = await getImageLibrary(buildingId, category);
      if (data && !error) {
        setImages(data.images);
      }
      setLoading(false);
    }
    fetchImages();
  }, [buildingId, category]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const { data, error } = await uploadImages(buildingId, Array.from(files), category);
    if (data && !error) {
      // Refresh images list
      const { data: refreshedData } = await getImageLibrary(buildingId, category);
      if (refreshedData) {
        setImages(refreshedData.images);
        // Auto-select the first uploaded image
        if (data.uploaded.length > 0) {
          const uploaded = data.uploaded[0];
          onSelect(uploaded.id, uploaded.image_url);
        }
      }
    }
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageClick = (image: ImageLibraryItem) => {
    if (selectedImageId === image.id) {
      // Deselect
      onSelect(null, null);
    } else {
      onSelect(image.id, image.image_url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading images...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Hero Image</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          multiple
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary-subtle rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload size={14} />
              Upload New
            </>
          )}
        </button>
      </div>

      {images.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg">
          <ImageIcon size={24} className="text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No images available</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Upload an image
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {images.map((image) => (
            <button
              key={image.id}
              type="button"
              onClick={() => handleImageClick(image)}
              className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                selectedImageId === image.id
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={image.image_url}
                alt={image.filename}
                className="w-full h-full object-cover"
              />
              {selectedImageId === image.id && (
                <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {selectedImageId && (
        <button
          type="button"
          onClick={() => onSelect(null, null)}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          <X size={12} className="inline mr-1" />
          Clear selection
        </button>
      )}
    </div>
  );
}
